import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { AdTagCreative, DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { CallToActionTypes, CreativeClass } from '@feathr/blackbox';
import {
  AdTagPreview,
  EmptyState,
  FacebookNewsfeedPreview,
  FacebookStoryPreview,
  InstagramFeedPreview,
  InstagramStoryPreview,
  Spinner,
  useVideoFrameExtract,
} from '@feathr/components';

import { getCreativeThumbnailUrl, hasStoredThumbnail } from '../../Creatives.utils';
import type { TSocialPreviewType } from '../SocialPreviewTypeSelector';
import SocialPreviewTypeSelector from '../SocialPreviewTypeSelector';
import ZoomControls from '../ZoomControls';
import { usePreviewPanel } from './PreviewPanel.hooks';

import * as styles from './PreviewPanel.css';

export interface IPreviewPanelProps {
  /** The creative model to preview. */
  creative?: DisplayCreative;
  className?: string;
}

const PLACEHOLDER_PAGE_NAME = 'Your Page';
const PLACEHOLDER_HEADLINE = 'Headline';
const PLACEHOLDER_WEBSITE_URL = 'example.com';

/**
 * Gets the CTA display label for a given call_to_action_type enum value.
 *
 * @param ctaType - The call_to_action_type value from the creative
 * @returns The human-readable label, or undefined if not found
 */
function getCtaLabel(ctaType: string | undefined): string | undefined {
  if (!ctaType) {
    return undefined;
  }
  return CallToActionTypes.find((cta) => cta.id === ctaType)?.name;
}

function renderSocialPreview(
  socialPreviewType: TSocialPreviewType,
  imageUrl: string | undefined,
  videoUrl: string | undefined,
  creative: FacebookDisplayCreative,
  t: TFunction,
): JSX.Element {
  const mediaProps = videoUrl ? { videoUrl } : { imageUrl: imageUrl! };
  const primaryText = creative.get('primary_text') || undefined;
  const headline = creative.get('headlines')?.[0] || PLACEHOLDER_HEADLINE;
  const websiteUrl = creative.get('display_link') || PLACEHOLDER_WEBSITE_URL;
  const ctaText = getCtaLabel(creative.get('call_to_action_type'));

  const commonProps = {
    ...mediaProps,
    pageName: PLACEHOLDER_PAGE_NAME,
    primaryText,
    ctaText,
    t,
  };

  switch (socialPreviewType) {
    case 'facebook_story':
      return <FacebookStoryPreview {...commonProps} />;

    case 'facebook_newsfeed':
      return (
        <FacebookNewsfeedPreview {...commonProps} headline={headline} websiteUrl={websiteUrl} />
      );

    case 'instagram_feed':
      return <InstagramFeedPreview {...commonProps} />;

    case 'instagram_story':
      return <InstagramStoryPreview {...commonProps} />;
  }
}

function PreviewPanel({ creative, className }: Readonly<IPreviewPanelProps>): JSX.Element {
  const { t } = useTranslation();

  const isAdTag = creative?.get('_cls') === CreativeClass.DisplayAdTag;
  const isMetaCreative = creative?.isFacebook;
  const isVideo = creative?.creativeType === 'Video';
  const contentWidth = creative?.get('width');
  const contentHeight = creative?.get('height');
  const creativeName = creative?.name;
  const adTagHtml = isAdTag ? (creative as AdTagCreative).get('adtag') : undefined;

  const hasThumbnail = creative ? hasStoredThumbnail(creative) : false;
  const creativeUrl = !isAdTag && creative ? getCreativeThumbnailUrl(creative) : undefined;
  const needsVideoExtract = isVideo && !hasThumbnail && !!creativeUrl;
  const {
    thumbnailUrl: videoFrameUrl,
    isLoading: isVideoThumbnailLoading,
    hasError: hasVideoThumbnailError,
  } = useVideoFrameExtract(creativeUrl ?? '', needsVideoExtract);

  const imageUrl = isVideo && !hasThumbnail ? videoFrameUrl || undefined : creativeUrl;
  const videoUrl = isVideo ? creative?.get('url') ?? undefined : undefined;

  const {
    zoom,
    socialPreviewType,
    viewportRef,
    contentRef,
    handleZoomChange,
    handleZoomToFit,
    handleSocialPreviewTypeChange,
  } = usePreviewPanel({ contentWidth, contentHeight });

  function renderPreview(): JSX.Element {
    if (
      (needsVideoExtract && hasVideoThumbnailError) ||
      (!imageUrl && !videoUrl && !(needsVideoExtract && isVideoThumbnailLoading) && !isAdTag)
    ) {
      return (
        <EmptyState
          description={t('Upload an image to see a preview here.')}
          label={t('No preview available')}
        />
      );
    }

    if (isAdTag) {
      return (
        <AdTagPreview
          height={contentHeight ?? 250}
          html={adTagHtml ?? ''}
          scale={zoom / 100}
          width={contentWidth ?? 300}
        />
      );
    }

    if (isMetaCreative && (imageUrl || videoUrl)) {
      return renderSocialPreview(
        socialPreviewType,
        imageUrl,
        videoUrl,
        creative as FacebookDisplayCreative,
        t,
      );
    }

    if (videoUrl) {
      return (
        /*
         * Using native video element instead of VideoPlayer component for consistency
         * with Meta social previews. This is a simple preview showing the creative content,
         * not a full-featured video player experience.
         */
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video className={styles.video} controls src={videoUrl} />
      );
    }

    return (
      <>
        {needsVideoExtract && isVideoThumbnailLoading && <Spinner />}
        <img
          alt={creativeName}
          className={classNames(
            styles.image,
            needsVideoExtract && isVideoThumbnailLoading && styles.hidden,
          )}
          src={imageUrl}
        />
      </>
    );
  }

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.toolbar}>
        {isMetaCreative && (
          <SocialPreviewTypeSelector
            onChange={handleSocialPreviewTypeChange}
            value={socialPreviewType}
          />
        )}
        <ZoomControls
          className={styles.zoomControls}
          onZoomChange={handleZoomChange}
          onZoomToFit={handleZoomToFit}
          zoom={zoom}
        />
      </div>
      <div className={styles.viewport} ref={viewportRef}>
        {isAdTag ? (
          <div className={styles.adTagContainer} ref={contentRef}>
            {renderPreview()}
          </div>
        ) : (
          <div className={styles.content} ref={contentRef}>
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(PreviewPanel);
